import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Typography,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useTheme } from 'styled-components';
import { IPlanViewModel } from 'viewModels';
import { IPlanInputModel, IPlanUpdateInputModel } from 'inputModels';
import { labelBillingCycle } from '../../types/enums';
import { usePlans } from '../../context/plans';
import { ModalPlanForm } from '../../components/Modals/ModalPlanForm';
import { ModalDeletePlan } from '../../components/Modals/ModalDeletePlan';
import { ModalPlanUsers } from '../../components/Modals/ModalPlanUsers';
import { Container, Header, ActionButtons, PlanCard, PriceDisplay } from './styles';
import { NavHeader } from '../../components/NavHeader';

export function Plans() {
  const theme = useTheme();
  const { plans, loading, error, createPlan, updatePlan, deletePlan, loadPlans, removeUserFromPlan, clearError } = usePlans();
  const [success, setSuccess] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<IPlanViewModel | null>(null);
  const [deletingPlan, setDeletingPlan] = useState<string | null>(null);
  const [viewingUsersPlan, setViewingUsersPlan] = useState<IPlanViewModel | null>(null);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleCreatePlan = async (data: IPlanInputModel) => {
    try {
      await createPlan(data);
      setSuccess('Plano criado com sucesso!');
      setOpenModal(false);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleUpdatePlan = async (data: IPlanUpdateInputModel) => {
    if (!editingPlan) return;

    try {
      await updatePlan(editingPlan.id, data);
      setSuccess('Plano atualizado com sucesso!');
      setOpenModal(false);
      setEditingPlan(null);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleSubmit = async (data: IPlanInputModel | IPlanUpdateInputModel) => {
    if (editingPlan) {
      await handleUpdatePlan(data as IPlanUpdateInputModel);
    } else {
      await handleCreatePlan(data as IPlanInputModel);
    }
  };

  const handleDeletePlan = async (planId: string) => {
    try {
      await deletePlan(planId);
      setSuccess('Plano excluído com sucesso!');
      setDeletingPlan(null);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  // Função para verificar se o plano pode ser excluído
  const canDeletePlan = (plan: IPlanViewModel) => {
    return !plan.users || plan.users.length === 0;
  };

  const handleEditPlan = (plan: IPlanViewModel) => {
    setEditingPlan(plan);
    setOpenModal(true);
  };

  const handleOpenModal = () => {
    setEditingPlan(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingPlan(null);
  };

  const handleViewUsers = (plan: IPlanViewModel) => {
    setViewingUsersPlan(plan);
  };

  const handleCloseUsersModal = () => {
    setViewingUsersPlan(null);
  };


  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };
  return (
    <>
      <NavHeader typeHeader="company" />
      <Container>
        <Header>
          <Typography variant="h4" component="h1" color={theme.colors.secondary}>
            Gerenciar Planos
          </Typography>
          <Button
            variant="contained"
            onClick={handleOpenModal}
            sx={{ backgroundColor: theme.colors.primary }}
          >
            + Novo Plano
          </Button>
        </Header>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {!loading && plans.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={8}
            textAlign="center"
          >
            <Typography variant="h5" color="text.secondary" gutterBottom>
              Nenhum plano encontrado
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              Não há planos cadastrados no sistema. Clique no botão "Novo Plano" para criar o primeiro plano.
            </Typography>
            <Button
              variant="contained"
              onClick={handleOpenModal}
              sx={{ backgroundColor: theme.colors.primary }}
            >
              + Criar Primeiro Plano
            </Button>
          </Box>
        ) : (
          <>
            {/* Planos Ativos */}
            {plans.filter(plan => !plan.disabled).length > 0 && (
              <Box mb={4}>
                <Typography variant="h5" component="h2" color={theme.colors.secondary} mb={3}>
                  Planos Ativos
                </Typography>
                <Grid container spacing={3}>
                  {plans.filter(plan => !plan.disabled).map((plan) => (
                    <Grid item xs={12} md={6} lg={4} key={plan.id}>
                      <PlanCard>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Typography variant="h6" component="h2" color={theme.colors.secondary}>
                              {plan.name}
                            </Typography>
                            <Chip
                              label="Ativo"
                              color="success"
                              size="small"
                            />
                          </Box>

                          <Typography variant="body2" color="text.secondary" mb={2}>
                            {plan.description}
                          </Typography>

                          <PriceDisplay>
                            <Typography variant="h4" component="span" color="primary">
                              {formatPrice(plan.price)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              /{labelBillingCycle[plan.billingCycle as keyof typeof labelBillingCycle]?.toLowerCase()}
                            </Typography>
                          </PriceDisplay>

                          <Box mt={2}>
                            <Typography variant="body2" color="text.secondary">
                              {plan?.users?.length} usuários
                            </Typography>
                          </Box>

                          <ActionButtons>
                            <IconButton
                              color="info"
                              onClick={() => handleViewUsers(plan)}
                              disabled={loading}
                              title="Visualizar usuários do plano"
                            >
                              <PeopleIcon />
                            </IconButton>
                            <IconButton
                              color="primary"
                              onClick={() => handleEditPlan(plan)}
                              disabled={loading}
                              title="Editar plano"
                            >
                              <EditIcon />
                            </IconButton>
                            <Tooltip
                              title={
                                canDeletePlan(plan)
                                  ? "Excluir plano"
                                  : "Não é possível excluir plano com usuários cadastrados"
                              }
                            >
                              <span>
                                <IconButton
                                  color="error"
                                  onClick={() => setDeletingPlan(plan.id)}
                                  disabled={loading || !canDeletePlan(plan)}
                                  title={canDeletePlan(plan) ? "Excluir plano" : "Não é possível excluir plano com usuários cadastrados"}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </ActionButtons>
                        </CardContent>
                      </PlanCard>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Planos Inativos */}
            {plans.filter(plan => plan.disabled).length > 0 && (
              <Box>
                <Typography variant="h5" component="h2" color={theme.colors.secondary} mb={3}>
                  Planos Inativos
                </Typography>
                <Grid container spacing={3}>
                  {plans.filter(plan => plan.disabled).map((plan) => (
                    <Grid item xs={12} md={6} lg={4} key={plan.id}>
                      <PlanCard>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Typography variant="h6" component="h2" color={theme.colors.secondary}>
                              {plan.name}
                            </Typography>
                            <Chip
                              label="Inativo"
                              color="default"
                              size="small"
                            />
                          </Box>

                          <Typography variant="body2" color="text.secondary" mb={2}>
                            {plan.description}
                          </Typography>

                          <PriceDisplay>
                            <Typography variant="h4" component="span" color="primary">
                              {formatPrice(plan.price)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              /{labelBillingCycle[plan.billingCycle as keyof typeof labelBillingCycle]?.toLowerCase()}
                            </Typography>
                          </PriceDisplay>

                          <Box mt={2}>
                            <Typography variant="body2" color="text.secondary">
                              {plan?.users?.length} usuários
                            </Typography>
                          </Box>

                          <ActionButtons>
                            <IconButton
                              color="info"
                              onClick={() => handleViewUsers(plan)}
                              disabled={loading}
                              title="Visualizar usuários do plano"
                            >
                              <PeopleIcon />
                            </IconButton>
                            <IconButton
                              color="primary"
                              onClick={() => handleEditPlan(plan)}
                              disabled={loading}
                              title="Editar plano"
                            >
                              <EditIcon />
                            </IconButton>
                            <Tooltip
                              title={
                                canDeletePlan(plan)
                                  ? "Excluir plano"
                                  : "Não é possível excluir plano com usuários cadastrados"
                              }
                            >
                              <span>
                                <IconButton
                                  color="error"
                                  onClick={() => setDeletingPlan(plan.id)}
                                  disabled={loading || !canDeletePlan(plan)}
                                  title={canDeletePlan(plan) ? "Excluir plano" : "Não é possível excluir plano com usuários cadastrados"}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </ActionButtons>
                        </CardContent>
                      </PlanCard>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </>
        )}

        {/* Modal de Criação/Edição */}
        <ModalPlanForm
          open={openModal}
          onClose={handleCloseModal}
          onSubmit={handleSubmit}
          editingPlan={editingPlan}
          loading={loading}
        />

        {/* Modal de Confirmação de Exclusão */}
        <ModalDeletePlan
          open={!!deletingPlan}
          onClose={() => setDeletingPlan(null)}
          onConfirm={async () => {
            if (deletingPlan) {
              const plan = plans.find((p: IPlanViewModel) => p.id === deletingPlan);
              if (plan && !canDeletePlan(plan)) {
                // Não permite exclusão se o plano tem usuários
                return;
              }
              await handleDeletePlan(deletingPlan);
            }
          }}
          loading={loading}
          planName={deletingPlan ? plans.find((p: IPlanViewModel) => p.id === deletingPlan)?.name : undefined}
          canDelete={deletingPlan ? canDeletePlan(plans.find((p: IPlanViewModel) => p.id === deletingPlan)!) : true}
          userCount={deletingPlan ? plans.find((p: IPlanViewModel) => p.id === deletingPlan)?.users?.length || 0 : 0}
        />

        {/* Modal de Visualização de Usuários */}
        <ModalPlanUsers
          open={!!viewingUsersPlan}
          onClose={handleCloseUsersModal}
          plan={viewingUsersPlan}
          loading={loading}
          onRemoveUser={removeUserFromPlan}
        />
      </Container>
    </>
  );
}